import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { StorageService, UtilsService } from 'src/app/services';
import { images } from '../../../../images';
import {
  CommunicationType,
  CommunicationUserType,
  CustomToggleTone,
  FilterField,
  FilterType,
  SuggestionSortType,
  UserRole,
} from 'src/app/models/enums';
import { ICustomToggleOptions, IFilterPanelValues } from 'src/app/models';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';
import {
  DEFAULT_SUGGESTION_SORT,
  deserializeMessagesFilters,
  deserializeSuggestionsFilters,
  parseMessagesFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
} from 'src/app/filters';

@Component({
  selector: 'app-communication-search-bar',
  templateUrl: './communication-search-bar.component.html',
  styleUrl: './communication-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommunicationSearchBarComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private queryParamsSub?: Subscription;

  @Input() searchValue = '';
  @Input() placeholder = '';
  @Input() searching = false;
  @Input() communicationType = CommunicationType.SUGGESTIONS;
  @Input() communicationUserType = CommunicationUserType.USERS;
  @Output() addCommunicationEventEmitter = new EventEmitter();
  @Output() filter = new EventEmitter<any>();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() sort = new EventEmitter();
  @Output() changeCommunicationUserTypeEmitter = new EventEmitter<CommunicationUserType>();
  @Output() changeCommunicationTypeEmitter = new EventEmitter();

  public filterOpened = false;
  public filtersApplied = 0;
  public filterTypes = FilterType;
  public messageFilterFields: FilterField[] = [
    FilterField.CLIENTS,
    FilterField.SENDERS,
    FilterField.RECEIVERS,
  ];
  public suggestionFilterFields: FilterField[] = [
    FilterField.CLIENTS,
    FilterField.AREAS_SERVICES_EXPEDITIONS,
    FilterField.SUGGESTION_TYPES,
    FilterField.SUGGESTION_PRIORITIES,
    FilterField.SUGGESTION_STATUS,
    FilterField.SUGGESTION_USER_RESOLUTIONS,
    FilterField.SUGGESTION_VISIBILITIES,
  ];

  public sortedOpened = false;
  public suggestionSortTypeSelected: SuggestionSortType =
    DEFAULT_SUGGESTION_SORT;
  public sortOptions: any[] = [];


  public images = images;

  public CommunicationTypes = CommunicationType;
  public CommunicationUserTypes = CommunicationUserType;
  readonly communicationTypeToggleOptions: ICustomToggleOptions<CommunicationType> = [
    {
      value: CommunicationType.SUGGESTIONS,
      image: images.suggestions.suggestionsIcon,
      descriptionKey: 'communications.types.suggestions',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: CommunicationType.MESSAGES,
      image: images.suggestions.messagesIcon,
      descriptionKey: 'communications.types.messages',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  readonly communicationUserTypeToggleOptions: ICustomToggleOptions<CommunicationUserType> = [
    {
      value: CommunicationUserType.USERS,
      image: images.suggestions.user,
      descriptionKey: 'users.userTypes.users',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: CommunicationUserType.DRIVERS,
      image: images.suggestions.driver,
      descriptionKey: 'users.userTypes.drivers',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  public adminRole: UserRole;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.getRole();
    this.updateFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => {
      this.applySortFromUrl();
      this.updateFilters();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['communicationType'] || changes['communicationUserType'])
      this.updateFilters();
  }

  ngAfterViewInit(): void {
    this.initSelectorOptions();
  }

  private applySortFromUrl() {
    const sortFromUrl = this.activatedRoute.snapshot.queryParamMap.get(
      'sort',
    ) as SuggestionSortType | null;
    this.suggestionSortTypeSelected = sortFromUrl ?? DEFAULT_SUGGESTION_SORT;
  }

  async getRole() {
    this.adminRole = await this.storageService.getRole();
    this.messageFilterFields = this.buildMessageFilterFields();
  }

  private buildMessageFilterFields(): FilterField[] {
    const fields = [FilterField.CLIENTS, FilterField.SENDERS, FilterField.RECEIVERS];
    if (this.adminRole === UserRole.SUPERADMIN || this.adminRole === UserRole.ADMIN) {
      fields.push(FilterField.MESSAGE_EVENTS, FilterField.MESSAGE_ORIGIN);
    }
    if (this.adminRole === UserRole.SUPERADMIN) fields.push(FilterField.MESSAGE_ADMIN_ALERTS);
    return fields;
  }

  async initSelectorOptions() {
    await this.applySortFromUrl();
    Object.values(SuggestionSortType).forEach(
      (sortType: SuggestionSortType) => {
        this.sortOptions.push({
          value: sortType,
          icon: images.suggestions.sortOptions[
            sortType.toLowerCase() as keyof typeof images.suggestions.sortOptions
          ],
          label: `suggestions.actions.sort.${sortType}`,
        });
      },
    );
  }

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  changeCommunicationUserType(userType: CommunicationUserType) {
    this.changeCommunicationUserTypeEmitter.emit(userType);
  }

  toggleCommunicationType() {
    this.changeCommunicationTypeEmitter.emit(
      this.communicationType === CommunicationType.SUGGESTIONS ? CommunicationType.MESSAGES : CommunicationType.SUGGESTIONS,
    );
  }

  async handleSort(newValue: SuggestionSortType) {
    this.suggestionSortTypeSelected = newValue;
    await this.storageService.setSuggestionsSortType(newValue);
    this.sort.emit();
  }

  closeFilter() {
    this.filterOpened = false;
  }

  onMessagesFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.MESSAGES]!.toFilters(values));
  }

  onSuggestionsFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.SUGGESTIONS]!.toFilters(values));
  }

  async updateFilters() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    let filtersForCount: any | null = null;
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      filtersForCount =
        params.keys.length > 0
          ? deserializeSuggestionsFilters(params)
          : parseSuggestionsFiltersFromStorage(
              await this.storageService.getSuggestionsFilters(),
            );
    } else if (this.communicationType === CommunicationType.MESSAGES) {
      filtersForCount =
        params.keys.length > 0
          ? deserializeMessagesFilters(params)
          : parseMessagesFiltersFromStorage(
              await this.storageService.getMessagesFilters(),
            );
    }
    this.filtersApplied = filtersForCount
      ? this.utilsService.getFiltersCount(filtersForCount)
      : 0;
  }
}
